import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import { cn } from '@/utils/cn';
import { PreviewFluidContext } from '../components/deviceFrameStyle';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { PlatformPreviewProps } from '../types';
import { PLATFORM_CONFIG } from '../platformConfig';
import { PreviewAvatar } from '../components/PreviewAvatar';
import { PreviewCaption } from '../components/PreviewCaption';
import { EmptyState } from '../components/EmptyState';
import { BlurFillImage } from '../components/BlurFillImage';
import { durationLabel, visualUrl } from '../previewMedia';

export const YouTubePreview = ({ caption, media, channel }: PlatformPreviewProps) => {
  const { t } = useTranslation('common');
  const config = PLATFORM_CONFIG.youtube;
  const fluid = useContext(PreviewFluidContext);
  const isEmpty = !caption && media.length === 0;

  const displayName = channel?.displayName ?? '';
  const options = channel?.options;
  const configuredTitle = options?.youtube?.title;
  const captionExcerpt = caption.split('\n').find((l) => l.trim())?.trim() ?? '';
  const videoTitle = configuredTitle || captionExcerpt;
  const isShort = options?.youtube?.isShort;
  const privacyStatus = options?.youtube?.privacyStatus || 'public';

  if (isEmpty) return <EmptyState />;

  const first = media[0];
  // Video-only chrome (play button, duration) only for an actual video; an
  // image shown as a format example gets none.
  const isVideo = !first || first.type === 'video';
  const duration = first && isVideo ? durationLabel(first.duration) : null;

  return (
    <div className={cn('bg-white w-full min-w-0 mx-auto rounded-xl border border-gray-200 shadow-sm p-2.5', fluid ? 'max-w-none' : 'max-w-[480px]')}>
      {/* Video Thumbnail */}
      <div
        className={`relative bg-gray-900 rounded-lg overflow-hidden ${isShort ? 'mx-auto aspect-[9/16] w-[170px]' : 'aspect-video'}`}
      >
        {first ? (
          <BlurFillImage src={visualUrl(first)} alt={t('preview_youtube_thumbnail_alt')} className="h-full w-full" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon name={ICONS.VIDEO} size="xl" color="text-gray-500" />
          </div>
        )}
        {/* Play button */}
        {isVideo && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-11 h-11 bg-black/60 rounded-full flex items-center justify-center">
              <div className="w-0 h-0 border-t-[7px] border-t-transparent border-l-[12px] border-l-white border-b-[7px] border-b-transparent ml-1" />
            </div>
          </div>
        )}
        {duration && (
          <div className="absolute bottom-1.5 right-1.5 bg-black/80 text-white text-[11px] font-medium px-1.5 py-0.5 rounded">
            {duration}
          </div>
        )}
        {isShort && (
          <div className="absolute top-2 left-2 bg-[#FF0000] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
            Shorts
          </div>
        )}
      </div>

      {/* Video Info */}
      <div className="flex gap-2.5 mt-2.5">
        <PreviewAvatar colorClass={config.avatarColor} size="md" avatarUrl={channel?.avatarUrl} initials={channel?.initials} />
        <div className="flex-1 min-w-0">
          {videoTitle && (
            <PreviewCaption
              caption={videoTitle}
              hashtagColor={config.hashtagColor}
              className={
                configuredTitle
                  ? 'text-sm font-medium leading-5 text-gray-900'
                  : 'text-sm leading-5 text-gray-600'
              }
              maxLines={2}
            />
          )}
          <p className="text-xs text-gray-600 mt-0.5 truncate">
            {displayName}
            {privacyStatus !== 'public' && (
              <span className="ml-1 text-gray-400">({privacyStatus})</span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};
